import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FacetSection, FacetSelectionChange, FacetValue } from './facet-panel.types';

/**
 * Sections of values with counts. Click selects only that value, shift-click adds or removes it, clicking the sole
 * selected value clears the section. Knows nothing about what the values mean.
 */
@Component({
  selector: 'shared-facet-panel',
  templateUrl: './facet-panel.component.html',
  styleUrls: ['./facet-panel.component.scss'],
  standalone: false,
})
export class FacetPanelComponent {
  @Input() sections: FacetSection[] = [];
  @Input() disabled = false;
  @Output() selectionChange = new EventEmitter<FacetSelectionChange>();

  private readonly searchTexts = new Map<string, string>();
  private readonly collapsed = new Set<string>();

  isCollapsed(section: FacetSection): boolean {
    return this.collapsed.has(section.name);
  }

  /** Folding a section away is a view choice only - the selection it holds stays active, and the header says so. */
  toggleCollapsed(section: FacetSection): void {
    if (this.collapsed.has(section.name)) {
      this.collapsed.delete(section.name);
    } else {
      this.collapsed.add(section.name);
    }
  }

  selectedCount(section: FacetSection): number {
    return section.selected?.length ?? 0;
  }

  getSearchText(section: FacetSection): string {
    return this.searchTexts.get(section.name) ?? '';
  }

  setSearchText(section: FacetSection, text: string): void {
    this.searchTexts.set(section.name, text ?? '');
  }

  visibleValues(section: FacetSection): FacetValue[] {
    const search = this.getSearchText(section).trim().toLowerCase();
    if (!search) {
      return section.values;
    }
    return section.values.filter(v => v.label?.toLowerCase().includes(search) || v.suffix?.toLowerCase().includes(search));
  }

  /** Values the server did not send because the section shows only the top N. */
  hiddenCount(section: FacetSection): number {
    return Math.max(0, (section.totalValues ?? section.values.length) - section.values.length);
  }

  isSelected(section: FacetSection, value: FacetValue): boolean {
    return section.selected?.includes(value.id) ?? false;
  }

  hasSelection(section: FacetSection): boolean {
    return (section.selected?.length ?? 0) > 0;
  }

  onValueClick(section: FacetSection, value: FacetValue, event: MouseEvent): void {
    if (this.disabled) {
      return;
    }
    const selected = section.selected ?? [];
    let next: string[];
    if (event.shiftKey) {
      next = selected.includes(value.id) ? selected.filter(id => id !== value.id) : [...selected, value.id];
    } else if (selected.length === 1 && selected[0] === value.id) {
      next = [];
    } else {
      next = [value.id];
    }
    this.selectionChange.emit({ name: section.name, selected: next });
  }

  clear(section: FacetSection): void {
    if (this.disabled) {
      return;
    }
    // clearing is not unfolding: a folded section stays folded, it just stops filtering
    this.selectionChange.emit({ name: section.name, selected: [] });
  }
}
